/**
 * AI-Guided Verification Page
 * Route wrapper for conversational AI verification interfaces
 * Automatically routes to General or Professional tier based on verification type
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import api from '../services/api';
import AIGuidedGeneralVerification from '../components/verifiers/AIGuidedGeneralVerification';
import AIGuidedProfessionalVerification from '../components/verifiers/AIGuidedProfessionalVerification';

const AIGuidedVerificationPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [verification, setVerification] = useState(null);
  const [verifierInfo, setVerifierInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadVerification();
    loadVerifierInfo();
  }, [id]);

  const loadVerification = async () => {
    try {
      const response = await api.get(`/simple-verifications/${id}`);
      if (response.data.success) {
        setVerification(response.data.data);
      }
    } catch (err) {
      console.error('Error loading verification:', err);
      setError('Failed to load verification details');
    } finally {
      setLoading(false);
    }
  };

  const loadVerifierInfo = async () => {
    try {
      const response = await api.get('/verifiers/profile/me');
      if (response.data.success) {
        setVerifierInfo(response.data.data);
      }
    } catch (err) {
      console.error('Error loading verifier info:', err);
    }
  };

  const handleComplete = (completedVerification) => {
    // Navigate to report page
    navigate(`/verifier/simple-verifications/${id}/report`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!verification) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Verification not found</Alert>
      </Box>
    );
  }

  // Determine tier based on verification data or verifier profile
  const tier = verification.tier || verifierInfo?.tier || 'general';

  // Route to appropriate interface
  if (tier === 'professional') {
    return (
      <AIGuidedProfessionalVerification
        verification={verification}
        verifierInfo={verifierInfo}
        onComplete={handleComplete}
      />
    );
  }

  return (
    <AIGuidedGeneralVerification
      verification={verification}
      onComplete={handleComplete}
    />
  );
};

export default AIGuidedVerificationPage;
